import React, { useState } from "react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";

export default function ProfileForm({ onSaved, showProgress = true }) {
  const { t, lang } = useI18n();
  const { user, updateProfile } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    gender: user?.gender || "",
    birth_year: user?.birth_year || "",
    birth_month: user?.birth_month || "",
    birth_day: user?.birth_day || "",
    birth_hour: user?.birth_hour ?? "",
    birth_minute: user?.birth_minute || 0,
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const requiredFields = ["name", "phone", "birth_year", "birth_month", "birth_day", "birth_hour"];
  const filled = requiredFields.filter((k) => form[k] !== "" && form[k] !== null && form[k] !== undefined).length;
  const pct = Math.round((filled / requiredFields.length) * 100);

  const save = async () => {
    for (const k of requiredFields) {
      if (form[k] === "" || form[k] === null || form[k] === undefined) {
        toast.error(lang === "zh" ? "请完整填写所有必填项" : "Please fill all required fields");
        return;
      }
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        gender: form.gender || "",
        birth_year: Number(form.birth_year),
        birth_month: Number(form.birth_month),
        birth_day: Number(form.birth_day),
        birth_hour: Number(form.birth_hour),
        birth_minute: Number(form.birth_minute) || 0,
      };
      await updateProfile(payload);
      toast.success(t("profileForm.saved"));
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const numField = (key, label, min, max, ph) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input type="number" min={min} max={max} placeholder={ph} value={form[key]}
        onChange={(e) => set(key, e.target.value)} data-testid={`profile-${key}`} />
    </div>
  );

  return (
    <div className="space-y-4">
      {showProgress && (
        <div>
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>{t("profileForm.completeness")}</span><span>{pct}%</span>
          </div>
          <Progress value={pct} className="h-2" data-testid="profile-progress" />
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 sm:col-span-1">
          <Label className="text-xs">{t("auth.name")}</Label>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="profile-name" />
        </div>
        <div className="col-span-2 sm:col-span-1">
          <Label className="text-xs">{t("auth.phone")}</Label>
          <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="profile-phone" />
        </div>
      </div>
      <div>
        <Label className="text-xs">{t("profileForm.gender")}</Label>
        <div className="flex gap-2 mt-1">
          {[["female", t("profileForm.female")], ["male", t("profileForm.male")]].map(([v, l]) => (
            <Button key={v} type="button" variant={form.gender === v ? "default" : "outline"}
              className={form.gender === v ? "bg-[hsl(var(--primary))] text-primary-foreground" : "border-border"}
              onClick={() => set("gender", v)} data-testid={`profile-gender-${v}`}>{l}</Button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs mb-1 block">{t("profileForm.birthDate")} ({t("profileForm.birthTime")})</Label>
        <div className="grid grid-cols-3 gap-2">
          {numField("birth_year", t("profileForm.year"), 1900, 2100, "1992")}
          {numField("birth_month", t("profileForm.month"), 1, 12, "3")}
          {numField("birth_day", t("profileForm.day"), 1, 31, "15")}
          {numField("birth_hour", t("profileForm.hour"), 0, 23, "8")}
          {numField("birth_minute", t("profileForm.minute"), 0, 59, "30")}
        </div>
      </div>

      <Button className="w-full bg-[hsl(var(--primary))] text-primary-foreground" disabled={busy} onClick={save} data-testid="profile-save-button">
        {t("common.save")}
      </Button>
    </div>
  );
}
